import React, { Suspense, useCallback, useEffect, useRef, useTransition } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useQueryLoader, usePreloadedQuery } from 'react-relay';

import { Skeleton as AvatarSkeleton } from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import ModalDialog, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { HierarchyChart, type HierarchyData } from '../HierarchyChart';
import { VerticalChartLine } from '../HierarchyChart/styles';

import InteractiveHierarchyModalQueryNode, {
	type InteractiveHierarchyModalQuery as InteractiveHierarchyModalQueryType,
} from './__generated__/InteractiveHierarchyModalQuery.graphql';

const messages = defineMessages({
	title: {
		id: 'reporting-lines.interactive-hierarchy-modal.title',
		defaultMessage: 'Reporting line',
		description: 'Title for the interactive org chart modal',
	},
});

const skeletonStyles = cssMap({
	cardContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		gap: token('space.100'),
		width: '200px',
	},
	cardContent: {
		flex: '1',
	},
	chartContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		paddingBlock: token('space.300'),
		opacity: token('opacity.loading'),
	},
	peersRow: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, max-content)',
		gap: token('space.100'),
	},
	pendingChart: {
		opacity: token('opacity.loading'),
		transition: 'opacity 150ms ease',
	},
	readyChart: {
		opacity: 1,
		transition: 'opacity 150ms ease',
	},
	modalBodyPadding: {
		paddingBlock: token('space.100'),
		minHeight: '200px',
	},
});

export interface InteractiveHierarchyModalProps {
	accountId: string;
	orgId: string;
	siteId: string;
	cloudId: string;
	onClose: () => void;
	testId?: string;
	/** The account ID of the currently logged-in user (viewer). Required to show the profile card on hover. */
	requestingUserId?: string;
}

function UserCardSkeleton() {
	return (
		<Box xcss={skeletonStyles.cardContainer}>
			<AvatarSkeleton size="medium" />
			<Box xcss={skeletonStyles.cardContent}>
				<AvatarSkeleton size="xsmall" />
			</Box>
		</Box>
	);
}

function HierarchyChartSkeleton() {
	return (
		<Box xcss={skeletonStyles.chartContainer}>
			<UserCardSkeleton />
			<VerticalChartLine />
			<UserCardSkeleton />
			<VerticalChartLine />
			<Box xcss={skeletonStyles.peersRow}>
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
				<UserCardSkeleton />
			</Box>
		</Box>
	);
}

type ReportingLineUser = {
	accountIdentifier?: string | null;
	numberOfDirectReports?: number | null;
	numberOfTotalReports?: number | null;
	pii?: {
		name?: string | null;
		picture?: string | null;
		extendedProfile?: { jobTitle?: string | null } | null;
	} | null;
};

function mapUser(userData: ReportingLineUser): HierarchyData['user'] | null {
	if (!userData?.accountIdentifier) {
		return null;
	}
	return {
		accountId: userData.accountIdentifier,
		name: userData.pii?.name ?? '',
		avatarUrl: userData.pii?.picture ?? null,
		jobTitle: userData.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: userData.numberOfDirectReports ?? undefined,
		numberOfTotalReports: userData.numberOfTotalReports ?? undefined,
	};
}

interface HierarchyChartLoaderProps {
	queryRef: Parameters<typeof usePreloadedQuery<InteractiveHierarchyModalQueryType>>[1];
	onNodeClick: (accountId: string) => void;
	cloudId: string;
	selectedAccountId: string;
	requestingUserId?: string;
	orgId?: string;
}

function HierarchyChartLoader({
	queryRef,
	onNodeClick,
	cloudId,
	selectedAccountId,
	requestingUserId,
	orgId,
}: HierarchyChartLoaderProps) {
	const data = usePreloadedQuery<InteractiveHierarchyModalQueryType>(
		graphql`
			query InteractiveHierarchyModalQuery($aaid: String!, $orgId: String!, $siteId: String!) {
				knowledgeDiscovery {
					reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
						@optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on KnowledgeDiscoveryReportingLines {
							user {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							managers {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							reports {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							peers {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const reportingLines = data.knowledgeDiscovery?.reportingLines;
	if (!reportingLines || reportingLines.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return null;
	}

	const user = reportingLines.user ? mapUser(reportingLines.user) : null;
	if (!user) {
		return null;
	}

	const managers = (reportingLines.managers ?? [])
		.map((manager) => mapUser(manager))
		.filter((manager): manager is HierarchyData['user'] => manager !== null);

	const reports =
		reportingLines.reports
			?.map((report) => mapUser(report))
			.filter((report): report is HierarchyData['user'] => report !== null) ?? [];

	const peers =
		reportingLines.peers
			?.map((peer) => mapUser(peer))
			.filter((peer): peer is HierarchyData['user'] => peer !== null) ?? [];

	const hierarchyData: HierarchyData = {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
		peers: peers.length ? peers : undefined,
	};

	return (
		<HierarchyChart
			data={hierarchyData}
			onNodeClick={onNodeClick}
			cloudId={cloudId}
			accountId={selectedAccountId}
			orgId={orgId}
			requestingUserId={requestingUserId}
		/>
	);
}

/**
 * Modal that renders an interactive, navigable org chart for a given user.
 * Clicking any user card re-fetches and re-centres the chart on that person.
 */
export function InteractiveHierarchyModal({
	accountId,
	orgId,
	siteId,
	cloudId,
	onClose,
	testId = 'interactive-hierarchy-modal',
	requestingUserId,
}: InteractiveHierarchyModalProps): React.JSX.Element {
	const intl = useIntl();
	const [queryRef, loadQuery] = useQueryLoader<InteractiveHierarchyModalQueryType>(
		InteractiveHierarchyModalQueryNode,
	);
	const [isPending, startTransition] = useTransition();
	const selectedAccountIdRef = useRef(accountId);

	useEffect(() => {
		loadQuery({ aaid: accountId, orgId, siteId });
		// loadQuery is stable from useQueryLoader — intentionally omitted from deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleNodeClick = useCallback(
		(clickedAccountId: string) => {
			selectedAccountIdRef.current = clickedAccountId;
			startTransition(() => {
				loadQuery({ aaid: clickedAccountId, orgId, siteId });
			});
		},
		[loadQuery, orgId, siteId],
	);

	return (
		<ModalDialog onClose={onClose} width="medium" testId={testId}>
			<ModalHeader hasCloseButton>
				<ModalTitle>{intl.formatMessage(messages.title)}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box xcss={skeletonStyles.modalBodyPadding}>
					<Suspense fallback={<HierarchyChartSkeleton />}>
						<Box xcss={isPending ? skeletonStyles.pendingChart : skeletonStyles.readyChart}>
							{queryRef && (
								<HierarchyChartLoader
									queryRef={queryRef}
									onNodeClick={handleNodeClick}
									cloudId={cloudId}
									selectedAccountId={selectedAccountIdRef.current}
									requestingUserId={requestingUserId}
									orgId={orgId}
								/>
							)}
						</Box>
					</Suspense>
				</Box>
			</ModalBody>
		</ModalDialog>
	);
}
